import React, { useEffect, useRef, useState } from 'react';

import { SearchOutlined } from '@ant-design/icons';
import { Button, Input, message } from 'antd';
import { T } from 'react-translator-component';
import styled from 'styled-components';

import { getTenantParentUserByCPF, getTenantParentUserByCNPJ } from '../../../hooks/users';
import { formatCpfOrCnpj, isCnpjInput, isValidCnpj, stripCnpj } from '../../../utils/cnpj/cnpj';

const { Search } = Input;

const FormLabelWrapper = styled.div`
  display: flex;
  .search_cpf_button {
    height: 30px;
    margin: 0 0 0px 10px;
  }

  .search_cpf_input {
    margin-left: 10px;
    width: 280px;
  }
`;

const SearchWrapper = styled.div`
  position: relative;
`;

const ResultsWrapper = styled.div`
  position: absolute;
  top: 100%;
  left: 10px;
  z-index: 1050;
  margin-top: 4px;
  width: max-content;
  min-width: 380px;
  max-height: 220px;
  overflow-y: auto;
  background: #fff;
  border-radius: 2px;
  box-shadow: 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08),
    0 9px 28px 8px rgba(0, 0, 0, 0.05);

  .result_item {
    position: relative;
    display: block;
    min-height: 32px;
    padding: 5px 12px;
    color: rgba(0, 0, 0, 0.85);
    font-weight: normal;
    font-size: 14px;
    line-height: 22px;
    white-space: nowrap;
    cursor: pointer;
    transition: background 0.3s ease;
  }

  .result_item:hover {
    background-color: #f5f5f5;
  }
`;

const SearchCpf = ({ chargeBoxPk, onSetNewValue }) => {
  const [inputCpf, setInputCpf] = useState(false);
  const [cpf, setCpf] = useState('');
  const [loadingCpf, setLoadingCpf] = useState(false);
  const [foundUsers, setFoundUsers] = useState([]);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (foundUsers.length === 0) return undefined;

    const closeOnClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setFoundUsers([]);
      }
    };

    document.addEventListener('mousedown', closeOnClickOutside);
    return () => document.removeEventListener('mousedown', closeOnClickOutside);
  }, [foundUsers]);

  const handleInputCpf = () => {
    setInputCpf((prevState) => !prevState);
    setFoundUsers([]);
  };

  const handleSelectUser = (user) => {
    setFoundUsers([]);
    onSetNewValue(user);
  };

  const rawDigits = stripCnpj(cpf);
  const isCnpj = isCnpjInput(cpf);

  const handleCpfChange = (e) => {
    setCpf(formatCpfOrCnpj(e.target.value));
  };

  const isValid = isCnpj
    ? isValidCnpj(rawDigits)
    : rawDigits.length === 11;

  const handleSearchCpf = async () => {
    setLoadingCpf(true);

    if (!isValid) {
      message.error(isCnpj ? T('Please enter a valid CNPJ to continue.') : T('Please enter a valid CPF to continue.'));
      setLoadingCpf(false);
      return;
    }

    const result = isCnpj
      ? await getTenantParentUserByCNPJ(chargeBoxPk, rawDigits)
      : await getTenantParentUserByCPF(chargeBoxPk, rawDigits);

    const users = result?.userPrivateStationList ?? [];

    if (result?.data?.error || users.length === 0) {
      setFoundUsers([]);
      message.error(T('User not found!'));
    } else if (users.length === 1) {
      handleSelectUser(users[0]);
    } else {
      setFoundUsers(users);
    }

    setLoadingCpf(false);
  };

  return (
    <FormLabelWrapper>
      <Button className='search_cpf_button' type='primary' onClick={handleInputCpf} icon={<SearchOutlined />}>
        {T('CPF/CNPJ')}
      </Button>

      {inputCpf && (
        <SearchWrapper ref={wrapperRef}>
          <Search
            value={cpf}
            maxLength={18}
            onChange={handleCpfChange}
            className='search_cpf_input'
            placeholder={T('Informe o CPF ou CNPJ completo')}
            enterButton={isValid ? T('Pesquisar') : <Button type='primary' style={{ cursor: 'default', opacity: 0.5 }}>{T('Pesquisar')}</Button>}
            size='middle'
            onSearch={handleSearchCpf}
            loading={loadingCpf}
          />

          {foundUsers.length > 1 && (
            <ResultsWrapper>
              {foundUsers.map((user) => (
                <div
                  key={user.user_pk}
                  className='result_item'
                  onClick={() => handleSelectUser(user)}
                >
                  {user.email} - {user.user_name} [{user.tenant_name}]
                </div>
              ))}
            </ResultsWrapper>
          )}
        </SearchWrapper>
      )}
    </FormLabelWrapper>
  );
};

export default SearchCpf;
